/**
 * @file ChatInput.tsx
 * @description 聊天输入与发送区域的主组件，负责：
 * - 文本/图片/文件的录入、拖拽上传与校验
 * - 语音识别入口与发送
 * - 与会话流（SSE）交互：创建消息、更新增量内容、溯源信息处理、思考时间计算
 * - 插件（工具）开关、联网搜索、思考模式等开关控制
 * - 图片与文件的本地上传、大小/类型校验与预览
 * - 音频播放（互斥控制）
 *
 * 关键设计：
 * - 使用 AbortController 控制一次会话的中断（停止回答）
 * - 使用 ref（isPausedRef）避免状态更新延迟造成的竞态
 * - 拖拽落下通过 BusEvent('chartFileDrop') 接收，覆盖层由外层 Chat/index.tsx 统一控制
 * - 输入框支持两种形态：常规 Textarea 与富文本（ChatSendInput）
 */
import MicrophoneIcon from '@/assets/microphone-icon.svg'
import { showMessage } from '@/components/antd-comp/message'
import { userContext } from '@/contexts/userContext'
import axios, { customFetch } from '@/controllers/request'
import PreviewPage from '@/pages/FileLibPage/components/preview'
import { FileInfoType } from '@/pages/FileLibPage/constants'
import { useBuriedEvent } from '@/util/buried'
import eventBus from '@/util/eventBus'
import { generateUUIDv4 } from '@/util/utils'
import { BusEvent } from '@/utils'
import type { UploadProps } from 'antd'
import { Divider, Tooltip, Upload, message } from 'antd'
import JSON5 from 'json5'
import { isEqual, uniqueId } from 'lodash'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Text } from 'slate'
import { showWarning } from '../antd-comp/message/useNotification'
import SwitchComponent from '../antd-comp/switch'
import AmrPlayer from '../audio/AmrPlayer'
import { PauseSvg } from '../audio/PauseSvg'
import { AudioPlaySvg } from '../audio/PlaySvg'
import { RunningSvg } from '../audio/RunningSvg'
import { Textarea } from '../bs-ui/input'
import ChatSendInput from './ChatSendInput'
import RecommendedCont from './RecommendedCont'
import SpeechRecognizer from './SpeechRecognizer'
import { checkVideoBenefit } from './api'
import { useChatStore } from './chatStore'
import WanxiangUpload from './components/WanxiangUpload'
import {
  MentINputDiffSVG,
  SendBsSendBtnSVG,
  UploadFileDefSVG,
  UploadImgDefSVG,
  WanxiangUploadSVG
} from './components/modelSvg'
import { ASSISTANT_STATUS, AgentStatus, CITATION_TYPE, ROLE_EMPTY_ERROR } from './constants'
import { useVideoTaskPolling } from './hooks/useVideoTaskPolling'
import './styles.less'
import { ChatMessageType, ChatResponseType } from './types'

import { generateUUID } from '@/utils'
import {
  ensureTextAtEnd,
  formatFileName,
  getFileIconByFileName,
  getFileType,
  getSpecificPluginResultMap,
  getValidChatMsg,
  parseHtmlToSlateValue
} from './utils'

/**
 * ChatInput 组件
 * @param onInputClick 点击输入区域的回调
 * @param clear 外部清理控制（未直接使用）
 * @param form 注入的表单内容（用于应用配置场景）
 * @param questions 推荐问题列表
 * @param inputForm 表单 ReactNode
 * @param chatUrl SSE 接口地址
 * @param assistantId 助手ID
 * @param conversation 是否处于会话中（影响样式与交互）
 * @param recommendedHid 推荐问题展示控制字段
 * @param regenerate 重试/再次提问回调
 * @param prompt 当前应用的 Prompt 前缀
 * @param conversationId 会话ID
 * @param disabled 是否禁用发送
 * @param tips 禁用提示文案
 * @param modelName 当前模型名称（影响思考模式开关等）
 * @param userName 用户名（用于错误提示拼接）
 * @param chattingFun 外部状态汇报（locked/again/stop 等）
 * @param supportSearch 是否支持联网搜索开关
 * @param thinking_mode 初始思考模式
 * @param recommendVisible 是否显示推荐问题
 * @param quickQuestions 外部一键问题
 * @param tool_ids 插件ID列表
 * @param tool_enable 插件开关
 * @param alterConversationId 临时会话ID（用于同步通知）
 */
export default function ChatInput({
  onInputClick,
  clear,
  form,
  questions,
  inputForm,
  chatUrl,
  assistantId,
  conversation,
  recommendedHid,
  regenerate,
  prompt = '',
  conversationId = '',
  disabled = false,
  tips = '',
  modelName = '',
  model_icon = '',
  deep_think_mode = 0,
  userName = '',
  // 会话中判断参数
  chattingFun,
  // 联网开关
  supportSearch = true,
  // 思考模式开关
  thinking_mode = 1,
  // 隐藏推荐部分
  recommendVisible = true,
  quickQuestions = '',
  tool_ids = [],
  tool_enable = false,
  // 替代id = 同步消息通知的id = 会话临时id
  alterConversationId = ''
}) {
  const { t } = useTranslation()
  const [formShow, setFormShow] = useState(false)
  const [showWhenLocked, setShowWhenLocked] = useState(false) // 强制开启表单按钮，不限制于input锁定
  const [inputLock, setInputLock] = useState({ locked: false, reason: '' })
  // 会话暂停参数已移到全局状态 useChatStore 中
  const controller = useRef(new AbortController()) as any // 定义控制器

  // 立即暂停ref，用于同步检查，避免状态更新延迟
  const isPausedRef = useRef(false)

  const currentConversationId = useRef(conversationId || alterConversationId || generateUUIDv4())

  // 视频任务轮询 Hook
  const { handleVideoTaskOutput, cleanupVideoTask, stopVideoTaskPolling, videoTaskIdRef } =
    useVideoTaskPolling(conversationId)

  // 将停止轮询函数存储到 store 中，以便 MessageBs 可以调用
  // 使用 ref 来存储最新的函数引用，避免 useEffect 依赖导致无限循环
  const stopVideoTaskPollingRef = useRef(stopVideoTaskPolling)
  stopVideoTaskPollingRef.current = stopVideoTaskPolling

  // 每次 stopVideoTaskPolling 变化时都更新 store
  useEffect(() => {
    // 获取 setStopVideoTaskPolling 函数（直接使用 getState 避免订阅导致重新渲染）
    const { setStopVideoTaskPolling } = useChatStore.getState()
    // 包装函数，通过 ref 访问最新的 stopVideoTaskPolling
    const wrappedFn = () => {
      if (stopVideoTaskPollingRef.current) {
        stopVideoTaskPollingRef.current()
      }
    }
    setStopVideoTaskPolling(wrappedFn)
    return () => {
      const { setStopVideoTaskPolling: cleanupSetStopVideoTaskPolling } = useChatStore.getState()
      cleanupSetStopVideoTaskPolling(undefined)
    }
  }, [stopVideoTaskPolling])

  const pluginStatusMap = getSpecificPluginResultMap(tool_ids, tool_enable)

  const currentToolIds = useRef(tool_ids)
  const currentToolEnabled = useRef(tool_enable)

  const ACCEPT_FILES = [
    'pdf',
    'docx',
    'doc',
    'ppt',
    'pptx',
    'wav',
    'mp3',
    'm4a',
    'ogg',
    'opus',
    'amr',
    'aac',
    'xlsx',
    'xls',
    'csv'
  ]
  const ACCEPT_FILES_DROP = [
    'pdf',
    'docx',
    'doc',
    'ppt',
    'pptx',
    'wav',
    'mp3',
    'm4a',
    'ogg',
    'opus',
    'amr',
    'aac',
    'xlsx',
    'xls',
    'csv',
    'jpg',
    'jpeg',
    'png'
  ]
  // const ACCEPT_FILES = pluginStatusMap.includedAudioPlugin
  //   ? ['pdf', 'docx', 'doc', 'ppt', 'pptx', 'png', 'jpg', 'jpeg', 'wav', 'mp3', 'm4a', 'ogg', 'opus', 'amr', 'aac']
  //   : ['pdf', 'docx', 'doc', 'ppt', 'pptx', 'png', 'jpg', 'jpeg']

  const FILE_UPLOAD_ACCEPT_FILES = [
    'pdf',
    'docx',
    'doc',
    'ppt',
    'pptx',
    'wav',
    'mp3',
    'm4a',
    'ogg',
    'opus',
    'amr',
    'aac',
    'xlsx',
    'xls',
    'csv'
  ]
  // const FILE_UPLOAD_ACCEPT_FILES = pluginStatusMap.includedAudioPlugin
  //   ? ['pdf', 'docx', 'doc', 'ppt', 'pptx', 'wav', 'mp3', 'm4a', 'ogg', 'opus', 'amr', 'aac']
  //   : ['pdf', 'docx', 'doc', 'ppt', 'pptx']

  // 临时增加用户信息处理
  const { user } = useContext(userContext)

  const {
    messages,
    chatId,
    limitInfo,
    updateChatId,
    createSendMsg,
    updateLoadingMessage,
    loadHistoryMsg,
    updateCurrentMessage,
    updateCurrentMessageImage,
    destroyChat,
    setShowGuideQuestion,
    updateCitationsMessage,
    setIsRunning,
    setUpIsLoading,
    setLastIsEndSuccess,
    setToolIds,
    setToolEnabled,
    running,
    setIsFix,
    is_fix,
    isPaused,
    setPaused,
    audioPlayingId,
    setAudioPlayingId
  } = useChatStore()

  const audioId = useRef(generateUUID(16))
  const currentChatIdRef = useRef(null)
  const inputRef = useRef(null)
  /* @input数据 */
  const sendInputRef = useRef(null)
  const inputValue = useRef('')
  /**
   * @state notify_info
   * @description @人功能携带的信息（选中成员ID、是否@所有人、企业ID）。
   */
  // @的携带信息
  const [notify_info, setNotifyInfo] = useState({
    // 所有id
    user_datas: [],
    // 是否编辑 正常发送false 编辑true
    is_fix: false,
    // 是否@所有人
    is_all: false,
    // 企业id
    enterpriseId: user.enterprise_id
  })

  // ------埋点------
  const { applicationBuriedEvent } = useBuriedEvent()

  /**
   * 记录会话切换状态，等待消息加载完成时，控制表单在新会话自动展开
   */
  const changeChatedRef = useRef(false)

  /* input判断显示组建 */
  const [MentInputDiff, setMentInputDiff] = useState(true)
  /* 针对CustomDrinkTextAreaLoading=default value只能初始化效果判断加载 没有value实时监听 */
  const [CustomDrinkTextAreaLoading, setCustomDrinkTextAreaLoading] = useState(false)
  /**
   * 切换富文本/常规输入形态时，触发内部重新挂载以刷新默认值渲染。
   */
  const CustomDrinkTextAreaLoadingFun = () => {
    // if (tool_ids.includes(10883) && MentInputDiff) {
    if (tool_ids.includes(10883) && tool_enable) {
      setMentInputDiff(false)
    } else {
      setMentInputDiff(true)
    }
    setCustomDrinkTextAreaLoading(false)
    setTimeout(() => {
      setCustomDrinkTextAreaLoading(true)
    }, 300)
  }
  /* 数据拆分 */
  /**
   * 从富文本节点中抽取纯文本与 HTML，同时收集 @用户 的 id 列表及是否 @所有人。
   */
  function extractTextFromNode(node: any): {
    plain: string
    html: string
    idList: string[]
    allType: string
  } {
    const idList: string[] = []
    let allType = ''
    function innerExtract(n: any): { plain: string; html: string } {
      if (Text.isText(n)) {
        return { plain: n.text, html: n.text }
      }

      if (n.type === 'mention') {
        const name = n.name || ''
        const id = n.key || n.id || ''
        if (id === 'all') {
          allType = 'all'
        } else if (id) {
          idList.push(id)
        }

        const html = `<span class="mention atSendMessage" data-id="${id}">@${name}</span>`
        return { plain: '@' + name, html }
      }

      if (Array.isArray(n.children)) {
        const children = n.children.map(innerExtract)
        return {
          plain: children.map(c => c.plain).join(''),
          html: children.map(c => c.html).join('')
        }
      }

      return { plain: '', html: '' }
    }

    const { plain, html } = innerExtract(node)

    return {
      plain,
      html,
      idList,
      allType
    }
  }

  /* 初始化 */
  // 初始化：清空输入值并重刷富文本组件
  useEffect(() => {
    inputValue.current = ''
    CustomDrinkTextAreaLoadingFun()
  }, [])
  // 会话切换：重置锁定/引导/插件等状态
  useEffect(() => {
    if (!chatId) return
    inputValue.current = ''
    CustomDrinkTextAreaLoadingFun()
    setInputLock({ locked: false, reason: '' })
    if (noVideo_task_output?.task_id) {
      chattingFun('')
    } else {
      chattingFun('again')
    }
    // chattingFun('again')
    setShowWhenLocked(false)
    currentChatIdRef.current = chatId
    changeChatedRef.current = true
    setFormShow(false)
    if (disabled) {
      setInputLock({ locked: true, reason: '' })
      chattingFun('locked')
    }
    setDisable_search(true)
    setThinking_mode_switch(thinking_mode)
    setLogoUrl('')
    setOriginalUrl('')
    setImgName('')
    clearUploadedFileInfo()
    clearWanXiangUploadedFileInfo()
  }, [chatId])

  // 输入锁定状态变化时，联动全局运行态
  useEffect(() => {
    if (changeChatedRef.current) {
      changeChatedRef.current = false
      // 新建的 form 技能,弹出窗口并锁定 input
      if (form && messages.length === 0) {
        setInputLock({ locked: true, reason: '' })
        chattingFun('locked')
        setFormShow(true)
        setShowWhenLocked(true)
      }
    }
  }, [messages])

  // assistantId / conversationId 变化：重置控制器、更新工具开关、销毁旧会话等
  useEffect(() => {
    setIsRunning(inputLock.locked)
  }, [inputLock])

  // 监听视频任务状态变化，如果任务完成（成功/失败/取消），解锁输入框
  useEffect(() => {
    // 检查最后一条助手消息中的视频任务状态
    const lastAssistantMessage = messages.filter((msg: any) => msg.role === 'assistant').slice(-1)[0]

    if (lastAssistantMessage?.content?.[0]?.generate_video) {
      const taskStatus = lastAssistantMessage.content[0].generate_video.task_status

      // 如果任务完成（成功/失败/取消），且输入框当前是锁定的，则解锁
      if (
        (taskStatus === 'SUCCEEDED' || taskStatus === 'FAILED' || taskStatus === 'CANCELED' || !taskStatus) &&
        inputLock.locked
      ) {
        setInputLock({ locked: false, reason: '' })
        chattingFun('')
      }
    }
  }, [messages, inputLock.locked, chattingFun])

  const uniqueInputId = useRef('')

  // 插件 ID 变更：刷新输入形态与工具集合
  useEffect(() => {
    inputValue.current = ''
    messageStoplink()
    /* conversationId 每次切换都为uuid 
    可以判断切换应用 因为开启新会话的assistantId重复 */
    updateChatId(assistantId)
    setToolIds(tool_ids)
    CustomDrinkTextAreaLoadingFun()
    setToolEnabled(tool_enable)
    uniqueInputId.current = uniqueId('chat_')
    if (conversationId && currentConversationId.current !== conversationId) {
      currentConversationId.current = conversationId
    }
    return () => {
      controller.current.abort()
      controller.current = new AbortController()
      destroyChat()
      uniqueInputId.current = ''
      setLogoUrl('')
      setOriginalUrl('')
      clearWanXiangUploadedFileInfo()
    }
  }, [assistantId, conversationId])

  // 插件开关变更：关闭时切回常规输入，开启时刷新富文本
  useEffect(() => {
    if (!isEqual(tool_ids, currentToolIds.current)) {
      currentToolIds.current = tool_ids
      CustomDrinkTextAreaLoadingFun()
      setToolIds(tool_ids)
    }
  }, [tool_ids])

  /* 开启关闭插件总开关 */
  useEffect(() => {
    if (tool_enable !== currentToolEnabled.current) {
      currentToolEnabled.current = tool_enable
      setToolEnabled(tool_enable)
    }
    console.log('111111111111111111111111')
    if (!tool_enable) {
      // 插件关闭时
      setMentInputDiff(true)
    } else {
      // 插件开启时
      inputValue.current = ''
      CustomDrinkTextAreaLoadingFun()
    }
  }, [tool_enable])

  /**
   * 中止当前会话请求：用于“停止回答”与会话切换，重置控制器与暂停状态。
   */
  function messageStoplink() {
    // 如果当前控制器已经被中止，则无需再次中止
    if (controller.current.signal.aborted) {
      console.log('信号已经中止，无需再次中止')
      return
    }

    // 中止当前控制器
    controller.current.abort()

    // 重置控制器
    controller.current = new AbortController()
    // 重置暂停状态（both ref and store state）
    isPausedRef.current = false
    setPaused(false)

    // 如果有正在进行的视频任务，取消它
    cleanupVideoTask()
  }

  // 对话信息更新
  // 监听消息数组：当最后一条是用户非占位消息时，发起会话请求
  useEffect(() => {
    if (MentInputDiff) {
      inputRef.current.value = ''
    } else {
      if (inputValue.current) {
        inputValue.current = ''
        CustomDrinkTextAreaLoadingFun()
      }
    }
    const len = messages.length
    if (len > 0 && messages[len - 1].role === 'user' && !messages[len - 1].fake) {
      // 检查是否已经有 loading 消息，避免重复创建
      const hasLoadingMessage = messages.some(msg => msg.role === 'loading')
      if (!hasLoadingMessage) {
        // 触发loading
        updateLoadingMessage()
      }
      // 对话
      handleChat(assistantId, messages, uniqueInputId.current)
    }
  }, [messages])

  /**
   * 语音识别结果回填输入框/富文本。
   */
  const handleRecognizedText = (text: string) => {
    if (MentInputDiff) {
      if (inputRef.current) {
        inputRef.current.value = text
      }
    } else {
      inputValue.current = text

      CustomDrinkTextAreaLoadingFun()
    }
  }

  /* 无内容提示 */
  const MessageNoneTitle = (title = '请输入内容') => {
    showMessage({
      content: title,
      type: 'warning'
    })
  }

  let clearWanXiangUploadedFileInfo = () => {
    setUploadedAudio(null)
    setUploadedVideos([])
    setLogoUrl('')
    setOriginalUrl('')
    setImgName('')
    // 同时清空 ref
    uploadedVideosRef.current = []
    uploadedAudioRef.current = null
    logoUrlRef.current = ''
    imgNameRef.current = ''
  }

  /**
   * 发送入口：
   * - 校验输入与附件
   * - 追加消息（文本/图片/文件）
   * - 锁定输入并滚动至底部
   */
  const handleSendClick = async (
    imageUrl = '',
    imageName = '',
    fileUrl = '',
    fileName = '',
    originalUrl = '',
    resendFiles: Array<{ fileUrl: string; fileName: string; fileId?: any }> = []
  ) => {
    if (!prompt) {
      showWarning('提示', ROLE_EMPTY_ERROR)
      return
    }
    let value = ''
    const messageNoneTitle = '请输入内容'
    if (MentInputDiff) {
      value = inputRef.current.value
    } else {
      value = inputValue.current
    }

    // 万相模型：内容校验 + 权益校验，全部通过后再修改 UI 状态
    if (isWanxiangModel) {
      if (value.trim() === '') {
        MessageNoneTitle(messageNoneTitle)
        return
      }
      // 校验视频生成权益，根据当前上传内容判断生成类型
      // uploadedVideos 有内容 → 视生视(v2v)；有图片 → 图生视(p2v)；默认 → 文生视(t2v)
      const generateType = uploadedVideos?.length > 0 ? 'v2v' : logoUrl || imageUrl ? 'p2v' : 't2v'
      try {
        const res = await checkVideoBenefit(generateType)
        if (!res?.check_status) {
          // 无权益，打开超限提示弹窗
          eventBus.emit('overLimits')
          return
        }
      } catch {
        // 接口异常同样拦截，避免无权益用户发出请求
        eventBus.emit('overLimits')
        return
      }
    }

    applicationBuriedEvent({
      page: 'Agent配置页面',
      event_name: '【提问发送】按钮',
      data_id: assistantId || null
    })

    // 解除锁定状态下 form 按钮开放的状态
    setShowWhenLocked(false)
    // 关闭引导词
    setShowGuideQuestion(false)
    // 收起表单
    // formShow && setFormShow(false)
    setFormShow(false)

    // 重发时可能通过事件直接传入多文件，优先使用传入值
    const normalizedResendFiles = resendFiles.filter(file => file && (file.fileUrl || file.fileName))
    // 非万相模型：判断是否有内容（文字、图片、文件等）
    if (!isWanxiangModel) {
      if (MentInputDiff) {
        if (
          value.trim() === '' &&
          !logoUrl &&
          !imageUrl &&
          !fileName &&
          uploadedFiles.length === 0 &&
          normalizedResendFiles.length === 0
        ) {
          MessageNoneTitle(messageNoneTitle)
          return
        }
      } else {
        if (
          value === '' &&
          !logoUrl &&
          !imageUrl &&
          !fileName &&
          uploadedFiles.length === 0 &&
          normalizedResendFiles.length === 0
        ) {
          MessageNoneTitle(messageNoneTitle)
          return
        }
      }
    }
    /* 会话数量限制 */
    // if (value.length > 2048) {
    if (value.length > 4096) {
      // showWarning('提示', '问题输入不能大于2048个字符')
      showWarning('提示', '问题输入不能大于4096个字符')
      return
    }

    const event = new Event('input', { bubbles: true, cancelable: true })
    if (MentInputDiff) {
      inputRef.current.value = ''
      inputRef.current.dispatchEvent(event) // 触发调节input高度
    } else {
      inputValue.current = ''
      CustomDrinkTextAreaLoadingFun()
    }
    // 通义万相模型：处理视频和音频
    if (isWanxiangModel) {
      let imgList = []
      if (logoUrl || imageUrl || logoUrlRef.current) {
        imgList = [
          {
            type: 'image',
            url: logoUrl || imageUrl || logoUrlRef.current,
            name: imgName || imageName || imgNameRef.current
          }
        ]
      }
      let audioList = []
      if (uploadedAudio?.url) {
        audioList = [uploadedAudio]
      } else if (uploadedAudioRef?.current?.url) {
        audioList = [uploadedAudioRef.current]
      }
      let videoList = []
      if (uploadedVideos?.length > 0) {
        videoList = uploadedVideos
      } else if (uploadedVideosRef?.current?.length > 0) {
        videoList = uploadedVideosRef.current
      }
      let material_info = [...imgList, ...videoList, ...audioList]
      createSendMsg('material', '', '', '', '', '', '', false, '', material_info)
      clearWanXiangUploadedFileInfo()
    } else {
      // 非通义万相模型：原有逻辑
      if (currentUploadFileType.current === 'image') {
        if (logoUrl || imageUrl) {
          createSendMsg(
            'image_url',
            '',
            logoUrl || imageUrl,
            imgName || imageName,
            '',
            '',
            '',   // 图片无 fileId
            null,
            OriginalUrl || originalUrl || logoUrl || imageUrl
          )
          setLogoUrl('')
          setOriginalUrl('')
          setImgName('')
        }
      } else if (currentUploadFileType.current === 'file') {
        // 每个文件各追加一条 file_url content 对象
        const filesToSend = normalizedResendFiles.length > 0 ? normalizedResendFiles : uploadedFiles
        if (filesToSend.length > 0) {
          filesToSend.forEach(f => {
            // 传入 fileId，存入消息内容，供重发/编辑时恢复 conversation_file_id
            createSendMsg('file_url', '', '', '', f.fileUrl, f.fileName, f.fileId)
          })
          clearUploadedFileInfo()
        } else if (fileUrl) {
          createSendMsg('file_url', '', '', '', fileUrl, fileName)
        }
      }
    }

    if (value) {
      console.log('value------------------444444444444---------------------', value)
      createSendMsg('text', value, '')
    }
    // 锁定 input
    setInputLock({ locked: true, reason: '' })
    chattingFun('locked')
    // 滚动聊天到底
    const messageDom = document.getElementById('message-panne')
    if (messageDom) {
      messageDom.scrollTop = messageDom.scrollHeight
    }
  }

  /**
   * 统一处理应用级错误码，向消息流与全局提示写入结果。
   */
  function handleApplicationErrors(jsonData, appId) {
    console.log('handleApplicationErrors>>>>>>>>>>', jsonData, appId)
    if (jsonData && (jsonData.code || jsonData.status_code)) {
      switch (jsonData.code) {
        case 4001:
          console.error('4001 : 应用已下线')
          updateCurrentMessage(appId, `该应用已下线 ，无法访问，请联系作者${userName}或超级管理员`, -1)
          showMessage({
            content: `该应用已下线 ，无法访问，请联系作者${userName}或超级管理员`,
            type: 'warning'
          })
          break
        case 4002:
          console.error('4002 : 应用已删除')
          updateCurrentMessage(appId, `该应用已删除，无法访问，请联系作者${userName}或超级管理员`, -1)
          showMessage({
            content: `该应用已删除，无法访问，请联系作者${userName}或超级管理员`,
            type: 'warning'
          })
          break
        case 4003:
          console.error('4003 : 应用已私密')
          updateCurrentMessage(appId, `该应用已私密，无法访问，请联系作者${userName}或超级管理员`, -1)
          showMessage({
            content: `该应用已私密，无法访问，请联系作者${userName}或超级管理员`,
            type: 'warning'
          })
          break
        default:
          console.error(`会话异常: ${jsonData.status_code}`)
          updateCurrentMessage(appId, `发生了助理解决不了的问题，工程师正在努力恢复，请稍等。`, -1)
          showMessage({
            content: `会话异常: ${jsonData.status_message || jsonData.status_code || jsonData.code}`,
            type: 'warning'
          })
          break
      }
      return true // 表示已经处理了特定错误代码，不再继续执行后续代码
    }
    return false
  }
  let noText = true // 当没有文本时，提示：发生了助理解决不了的问题，工程师正在努力恢复，请稍等。
  let noImage = true // 当没有图片时，提示：发生了助理解决不了的问题，工程师正在努力恢复，请稍等。
  let noVideo_task_output: { task_id?: string; task_status?: string } = {} // 当没有视频任务输出时，提示：发生了助理解决不了的问题，工程师正在努力恢复，请稍等。
  /**
   * 核心：发起 SSE 会话并流式处理：
   * - 透传最近上下文的 tool_calls 给最后一条 assistant
   * - 解析 data 行：增量文本/图片、溯源（工具/知识/MCP）、思考内容
   * - 计算思考时长（思考首/末帧时间差）并补充到消息
   * - 支持 Abort 停止、断点安全清理
   */
  const handleChat = async (appId: string, msg: ChatMessageType[], uid) => {
    chattingFun('')
    isStopHandled.current = false // 重置停止处理标志位，开始新请求
    isPausedRef.current = false // 重置立即暂停ref
    setPaused(false) // 重置停止状态
    const chatMsg = getValidChatMsg(msg, prompt, limitInfo)
    console.log(chatMsg, ' chatMsg')

    const signal = controller.current.signal

    // 从最后一条用户消息的 file_url 列表里取第一个文件的 file_id
    const lastMsgContent = chatMsg[chatMsg.length - 1]?.content ?? []
    const firstFileItem = lastMsgContent.find((c: any) => c?.type === 'file_url' && c?.file_url?.file_id)
    const currentFileId = firstFileItem?.file_url?.file_id || undefined

    let tool_calls = []
    if (messages && messages.length > 0) {
      // 取最后三条（或不足三条就取全部）
      const lastThree = messages.slice(-3)
      for (let i = lastThree.length - 1; i >= 0; i--) {
        const msg = lastThree[i]
        if (msg?.role === 'citations' && msg?.citations?.[0]?.tool_calls) {
          tool_calls = msg.citations[0].tool_calls
          break
        }
      }
    }
    let messageCopy = [...chatMsg]
    if (messageCopy.length > 0) {
      // 取最后三条（或全部）
      const lastThree = messageCopy.slice(-3)
      for (let i = lastThree.length - 1; i >= 0; i--) {
        const msg = lastThree[i]
        if (msg?.role === 'assistant') {
          const actualIndex = messageCopy.length - lastThree.length + i
          messageCopy[actualIndex] = {
            ...messageCopy[actualIndex],
            tool_calls
          }
          break
        }
      }
    }

    try {
      setLastIsEndSuccess(false)

      // 构建请求体
      const requestBody: any = {
        app_id: appId,
        is_preview: 1,
        messages: messageCopy,
        sso_key: 'test',
        conversation_id: conversationId,
        disable_search: !disable_search,
        thinking_mode: thinking_mode_switch,
        is_regenerate,
        conversation_file_id: currentFileId,
        'chat-alias-id': currentConversationId.current,
        extendInfo: {
          enterpriseUser: {
            name: user.name || user.user_name // 用户昵称或者用户名称用来显示
          },
          // 非at人的那轮消息不传递 notify_info
          notify_info:
            !notify_info.is_all && notify_info.user_datas.length === 0
              ? {}
              : {
                  ...notify_info,
                  is_fix
                }
        }
      }

      // 如果是通义万相模型，添加 generate_video_payload
      if (isWanxiangModel) {
        // 构建 generate_video_payload
        // 从 wanxiangConfig 获取配置值
        const {
          smartRewrite, // 智能改写
          resolution, // 分辨率：'1080P' 或 '720P'
          duration, // 视频时长：'5'、'10'、'15'
          cameraMode, // 镜头模式：'single'（单镜头）或 'multi'（多镜头）
          generateAudio // 生成音频：true/false
        } = wanxiangConfig

        // 判断模型类型：根据最后一条用户消息中的文件类型
        // 视频生视频：wan2.6-r2v
        // 图生视频：wan2.6-i2v
        // 文生视频：wan2.6-t2v
        let videoModel = 'wan2.6-t2v' // 默认文生视频
        if (messageCopy.length > 0) {
          const lastMessage = messageCopy[messageCopy.length - 1]
          if (lastMessage?.role === 'user' && lastMessage?.content) {
            let hasVideo = false
            let hasImage = false

            // 遍历 content 数组，检查 material_info 和 image_url
            for (const content of lastMessage.content) {
              // 检查 material_info 中的视频
              if (content.material_info && Array.isArray(content.material_info)) {
                for (const item of content.material_info) {
                  if (item.type === 'video') {
                    hasVideo = true
                    break
                  }
                  if (item.type === 'image') {
                    hasImage = true
                  }
                }
              }
              // 检查 image_url 类型
              if (content.type === 'image_url' && content.image_url?.url) {
                hasImage = true
              }
            }

            if (hasVideo) {
              videoModel = 'wan2.6-r2v' // 视频生视频
            } else if (hasImage) {
              videoModel = 'wan2.6-i2v' // 图生视频
            }
          }
        }

        requestBody.generate_video_payload = {
          model: videoModel,
          parameters: {
            // 分辨率：720P 或 1080P
            resolution: resolution,
            // 是否开启 prompt 智能改写，默认值 true
            prompt_extend: smartRewrite,
            // 视频时长（秒）：5、10、15，默认值 5
            duration: duration,
            // 是否为生成的视频自动添加音频，默认值 true
            // 生效条件：仅在不传入 audio_url 时生效
            audio: generateAudio,
            // 镜头类型：single（单镜头）或 multi（多镜头），默认值 single
            // 生效条件：仅当 "prompt_extend": true 时生效
            shot_type: cameraMode
          }
        }
      }

      const response = await customFetch(chatUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'app-id': appId,
          Accept: 'text/event-stream'
        },
        body: JSON.stringify(requestBody),
        signal,
        credentials: 'include'
      })
      // 发送消息后重置状态 false正常发送 点击编辑=true
      setIsFix(false)
      // 检查HTTP状态码
      if (!response.ok) {
        throw new Error(`请求失败，状态码: ${response.status}`)
      }

      const contentType = response.headers.get('Content-Type')
      if (contentType?.includes('application/json')) {
        try {
          // 使用克隆的响应对象来读取一次性的文本内容
          const clonedResponse = response.clone()
          const text = await clonedResponse.text()
          if (text) {
            const jsonData = JSON5.parse(text)
            if (handleApplicationErrors(jsonData, appId)) return // 如果有特定错误代码，直接返回
          }
        } catch (parseError) {
          console.log(parseError)
          // throw new Error('解析响应体失败')
        }
      }
      const reader = response.body.getReader()
      const decoder = new TextDecoder('utf-8')

      const allCitations = []

      let startThinkTime: number = 0
      let endThinkTime: number = 0
      let thinkTimeFull: boolean = false

      /**
       * 在对话过程中判断应该用哪个方法来更新
       * @param content
       * @param updateParams
       */
      /**
       * 根据不同的更新类型（文本/图片/通知）分发到对应的更新方法，
       * 并在内部打入停止标记，以便“停止回答”时补充思考停止信息。
       */
      const updateCore = (
        { updateType, content },
        ..._updateParams: [string, string, number?, any?, boolean?, string?, number?]
      ) => {
        // 立即检查ref，避免状态更新延迟
        if (isPausedRef.current || isPaused) {
          return
        }

        /* 保存最后记忆状态 */
        stopMessInfo.current = _updateParams
        const chatStopTag = isPausedRef.current || isPaused ? 'stop' : ''
        const [chatId, text, status, internetCitations, clear, think, thinkTime] = _updateParams

        switch (updateType) {
          case 'text':
            updateCurrentMessage(chatId, text, status, internetCitations, clear, think, thinkTime, chatStopTag)
            break
          case 'viaual':
            updateCurrentMessageImage(
              content?.detail?.imgUrls?.[0],
              chatId,
              text,
              status,
              internetCitations,
              clear,
              think,
              thinkTime,
              chatStopTag
            )
            break
          case 'notify':
            const hitPlugin = { ...content?.hitPlugin, fix_num: content?.detail?.fix_num }
            updateCurrentMessage(
              chatId,
              text,
              status,
              internetCitations,
              clear,
              think,
              thinkTime,
              chatStopTag,
              hitPlugin
            )
            break
          default:
            updateCurrentMessage(chatId, text, status, internetCitations, clear, think, thinkTime, chatStopTag)
            break
        }
      }
      /**
       * 处理 SSE 的一行数据：
       * - data: {json} 增量解析
       * - data: [DONE] 结束标记
       * - data: Error 错误透传
       */
      const processLine = (line: string) => {
        // 立即检查ref，避免状态更新延迟
        if (isPausedRef.current || isPaused) {
          return false
        }

        if (line.startsWith('data: {')) {
          try {
            const jsonData: ChatResponseType = JSON5.parse(line.slice(6))
            // jsonData.choices = mockData?.choices

            const choice = jsonData?.choices?.[0]
            let content = choice?.delta?.content
            let updateType = 'text'
            try {
              content = JSON.parse(choice?.delta?.content)
              updateType = (content as any)?.msgType || 'text'
            } catch (err) {
              // console.log('err', err)
            }

            let internetCitations
            /**
             * 溯源信息处理
             */
            if (jsonData.citations && jsonData.citations.length > 0) {
              jsonData.citations.forEach(citation => {
                if (
                  citation.type === CITATION_TYPE.knowledge ||
                  citation.type === CITATION_TYPE.plugin ||
                  citation.type === CITATION_TYPE.mcp
                )
                  // updateCitationsMessage(appId, citation)
                  allCitations.push({ appId, citation })
              })
            }
            if (ASSISTANT_STATUS.includes(jsonData.status)) {
              /**
               * 这里临时由前端增加解决方案
               * 同时存在两条溯源信息时，只显示工具的溯源信息，后期需要后端修改返回，未使用的溯源不返回
               * allCitations判断在后端修改后移除
               */
              if (allCitations.length > 0) {
                let validCitations = [...allCitations]
                allCitations.length = 0

                const citationTypes = Array.from(new Set(validCitations.map(item => item.citation.type)))
                if (citationTypes.length > 1) {
                  validCitations = validCitations.filter(item => item.citation.type === CITATION_TYPE.plugin)
                }

                validCitations.forEach(item => {
                  updateCitationsMessage(item.appId, item.citation)
                })
              }
              // 思考过程处理
              const think = jsonData.choices?.reduce((acc, choice) => acc + (choice.delta?.reasoning_content || ''), '')
              /** 会话消息处理 */
              const text = jsonData.choices?.reduce((acc, choice) => {
                if (updateType === 'viaual') {
                  const content = choice?.delta?.content ? JSON.parse(choice?.delta?.content) : ''
                  return acc + content?.detail?.content || ''
                } else if (updateType === 'notify') {
                  const content = choice?.delta?.content ? JSON.parse(choice?.delta?.content) : ''
                  return acc + content?.detail?.content || ''
                } else {
                  try {
                    // 防止文生图 给md形式的回复，但是类型不是viaual
                    const content = JSON.parse(choice?.delta?.content)?.content
                    return acc + typeof content === 'string' ? content : choice.delta?.content || ''
                  } catch (err) {}
                }
                return acc + (choice.delta?.content || '')
              }, '')
              /**
               * 图片消息处理
               */
              const img = jsonData.choices?.reduce((acc, choice) => {
                if (updateType === 'viaual') {
                  const content = JSON.parse(choice.delta?.content)
                  const imgs = content?.detail?.imgUrls
                  if (imgs?.length > 0) {
                    noImage = false
                  }
                  return acc.concat(imgs)
                }
                return acc
              }, [])
              /* 视频任务输出 */
              noVideo_task_output =
                jsonData.citations.length === 1 && (jsonData.citations[0] as any)?.video_task_output
                  ? (jsonData.citations[0] as any)?.video_task_output
                  : {}
              /* 联网搜索 */
              const hasInternetCitation = jsonData.citations.some(citation => citation.type === 'internet')
              if (think && !startThinkTime) {
                startThinkTime = jsonData.created
              }
              if ((text || img.length) && !endThinkTime) {
                endThinkTime = jsonData.created
              }
              if (think || text || img.length) {
                noText = false
                /* citations =  type = 'internet' 联网搜索 */
                if (hasInternetCitation) {
                  internetCitations = jsonData.citations
                }
                // chatStopTag 现在在 updateCore 内部计算
                if (jsonData.status === AgentStatus.content_illegal) {
                  updateCore(
                    {
                      updateType,
                      content
                    },
                    appId,
                    text,
                    jsonData.status,
                    internetCitations,
                    true,
                    '', // think
                    0 // thinkTime
                  )
                  return true
                } else {
                  // 临时填充思考时间
                  if (endThinkTime && startThinkTime && !thinkTimeFull) {
                    thinkTimeFull = true
                    updateCore(
                      {
                        updateType,
                        content
                      },
                      appId,
                      '',
                      jsonData.status,
                      internetCitations,
                      false,
                      '',
                      endThinkTime - startThinkTime
                    )
                  }
                  updateCore(
                    {
                      updateType,
                      content
                    },
                    appId,
                    text,
                    jsonData.status,
                    internetCitations,
                    false,
                    think,
                    0 // thinkTime
                  )
                }
              }
              // 如果有视频任务输出，且没有文本/思考/图片，直接处理视频任务，不创建空消息
              if (noVideo_task_output?.task_id) {
                handleVideoTaskOutput(noVideo_task_output, appId)
              }
            }
          } catch (e) {
            console.error('解析 JSON 失败:', e)
            chattingFun('')
          }
        } else if (line === 'data: [DONE]') {
          console.log(noText, 'noText')
          console.log(noImage, 'noImage')
          // if (noText && noImage) {
          if (noText && noImage && !noVideo_task_output?.task_id) {
            /* 视频生成失败，出现异常，请稍后重试 */
            if (
              noVideo_task_output &&
              noVideo_task_output.task_status === 'FAILED' &&
              noVideo_task_output.task_id === ''
            ) {
              updateCurrentMessage(appId, '生成任务失败，出现异常，请稍后重试', -1)
              chattingFun('')
              return
            }
            /* 兜底报错 */
            updateCurrentMessage(appId, '发生了助理解决不了的问题，工程师正在努力恢复，请稍等。', -1)
            chattingFun('')
          }
          return true
        } else if (line.startsWith('data: Error')) {
          // 将异常处理文本内容直接输出到对话列表
          updateCurrentMessage(appId, line.substring(6), -1)
        }
      }
      chattingFun('stop')

      let buffer = ''
      // 持续读取流：注意在多处检查暂停/中止状态以避免竞态
      while (true) {
        if (isPausedRef.current || isPaused) break
        const { done, value } = await reader.read()
        if (isPausedRef.current || isPaused || done) break
        if (uid !== uniqueInputId.current) break

        const chunk = decoder.decode(value, { stream: true })
        if (isPausedRef.current || isPaused) break

        // 如果已停止，清空缓冲区并跳出
        if (isPausedRef.current || isPaused) {
          buffer = ''
          break
        }

        buffer += chunk
        const lines = buffer.split('\r\n')
        buffer = lines.pop() || ''

        if (isPausedRef.current || isPaused) {
          buffer = '' // 清空缓冲区
          break
        }
        for (const line of lines) {
          if (isPausedRef.current || isPaused) {
            buffer = '' // 清空缓冲区
            break
          }
          if (processLine(line)) break
        }
      }
      console.log('数据处理结束')
      setLastIsEndSuccess(true)
    } catch (error) {
      console.error('请求过程中出现问题:', error)
      // 如果是用户主动停止（AbortError），则不作为错误处理
      if (error.name === 'AbortError' && (isPausedRef.current || isPaused)) {
        console.log('用户主动停止了请求')
        return
      }
      // 如果是其他情况下的暂停
      if (isPausedRef.current || isPaused) {
        if (noText && noImage && !noVideo_task_output?.task_id) {
          updateCitationsMessage(appId, {}, 'clear')
          updateCurrentMessage(appId, '回答已暂停', -1)
        }
        return
      }
      // 其他错误情况
      updateCurrentMessage(appId, '发生了助理解决不了的问题，工程师正在努力恢复，请稍等。', -1)
    } finally {
      // 重置控制器和状态
      controller.current = new AbortController()
      isPausedRef.current = false // 重置立即暂停ref
      setPaused(false)
      isStopHandled.current = false // 重置停止处理标志位
      if (noVideo_task_output?.task_id) {
        setInputLock({ locked: true, reason: '' })
        chattingFun('locked')
      } else {
        setInputLock({ locked: false, reason: '' })
        chattingFun('again')
      }
      setIs_regenerate(false)
    }
  }

  // 停止相关：记录最后一次 update 参数，便于停止时补写“已停止深度思考”等信息
  const stopMessInfo = useRef<[string, string, number?, any?, boolean?, string?, number?]>([] as any)
  const isStopHandled = useRef(false) // 添加标志位防止重复处理停止逻辑

  // 监听重发消息事件
  // 自定义事件：外部触发“重发/停止”等操作（含图片直发）
  useEffect(() => {
    const handleCustomEvent = e => {
      // 中断会话 detail.send = 'stop'
      if (e.detail.send === 'stop') {
        // 防止重复处理
        if (isStopHandled.current) {
          console.log('停止操作已处理，跳过重复执行')
          return
        }

        // 立即设置停止状态，确保同步生效
        isPausedRef.current = true
        useChatStore.getState().isPaused = true
        setPaused(true)
        isStopHandled.current = true

        try {
          controller.current.abort() // 中断请求
        } catch (err) {
          console.log('controller已经被中止:', err)
        }

        /* 停止会话 - 如果思考中 - 停止思考- 深度思考 -6.10 */
        const args = stopMessInfo.current
        if (args.length > 0) {
          // 填充参数，确保至少前 9 个为正常参数位
          const fullArgs = [...args] as [string, string, number?, any?, boolean?, string?, number?, string?, any?]
          while (fullArgs.length < 9) {
            fullArgs.push(undefined)
          }
          // 合并停止信息，只调用一次updateCurrentMessage
          const thinkInfo = {
            type: 'stop',
            text: '已停止深度思考'
          }
          // 安全地添加停止标志，避免直接修改数组
          updateCurrentMessage(
            fullArgs[0], // chatId
            fullArgs[1], // content
            fullArgs[2], // status
            fullArgs[3], // internetCitations
            fullArgs[4], // clear
            fullArgs[5], // think
            fullArgs[6], // thinkTime
            '', // chatStopTag
            fullArgs[8], // hitPlugin
            thinkInfo // thinkInfo
          )
        }
        return
      }
      if (!showWhenLocked && inputLock.locked) {
        console.error('弹窗已锁定，消息无法发送')
        return
      }
      const { send, message, imageUrl, imageName, originalUrl, materialInfo, files, fileUrl, fileName } = e.detail
      // 兼容新旧两种字段：新字段 files（多文件）优先，旧字段 fileUrl/fileName 兜底
      const resendFiles =
        Array.isArray(files) && files.length > 0
          ? files.filter(file => file && (file.fileUrl || file.fileName))
          : fileUrl || fileName
          ? [{ fileUrl: fileUrl || '', fileName: fileName || '' }]
          : []

      // 重发时，先停止之前的视频任务轮询
      // 只停止轮询，不调用 cleanupVideoTask，避免查询旧任务状态
      // 因为重发时会立即发起新请求，不需要取消旧任务
      if (send) {
        stopVideoTaskPolling()
        // 清空任务 ID，避免后续的 cleanupVideoTask 查询旧任务
        videoTaskIdRef.current = null
      }

      if (MentInputDiff) {
        inputRef.current.value = message
      } else {
        inputValue.current = message
        CustomDrinkTextAreaLoadingFun()
      }
      console.log('materialInfo------------------111111111111---------------------', materialInfo)
      // 处理通义万相模型的 material_info
      if (materialInfo && Array.isArray(materialInfo) && materialInfo.length > 0) {
        currentUploadFileType.current = 'material'
        // 分离不同类型的文件
        const videos = materialInfo.filter(item => item.type === 'video')
        const audio = materialInfo.find(item => item.type === 'audio') || null
        const image = materialInfo.find(item => item.type === 'image')
        // 同时设置状态和 ref，确保能立即读取到值
        setUploadedVideos(videos)
        setUploadedAudio(audio)
        uploadedVideosRef.current = videos
        uploadedAudioRef.current = audio
        if (image) {
          setLogoUrl(image.url)
          setOriginalUrl(image.url)
          setImgName(image.name || '')
          logoUrlRef.current = image.url
          imgNameRef.current = image.name || ''
        } else {
          logoUrlRef.current = ''
          imgNameRef.current = ''
        }
        if (send) {
          // 直接调用，使用 ref 中的值
          handleSendClick('', '', '', '', '')
        }
      } else {
        // 非通义万相模型，使用原有逻辑
        if (resendFiles.length > 0) {
          currentUploadFileType.current = 'file'
          const uiFiles = resendFiles.map(file => ({
            id: generateUUID(16),
            fileId: file.fileId || '',
            fileName: file.fileName || '',
            fileUrl: file.fileUrl || ''
          }))
          // 编辑态回显文件列表；重发时同时复用这些文件发送
          setUploadedFiles(uiFiles)
          setLogoUrl('')
          setOriginalUrl('')
          setImgName('')
          if (send) {
            handleSendClick('', '', '', '', '', resendFiles)
          }
        } else {
          currentUploadFileType.current = 'image'
          clearUploadedFileInfo()
          if (send) {
            handleSendClick(imageUrl, imageName, '', '', originalUrl)
          } else {
            setLogoUrl(imageUrl)
            setOriginalUrl(originalUrl)
            setImgName(imageName)
          }
        }
      }
    }
    document.addEventListener('userResendMsgEvent', handleCustomEvent)
    return () => {
      document.removeEventListener('userResendMsgEvent', handleCustomEvent)
    }
  }, [inputLock.locked, showWhenLocked])

  // message
  const noLimitsMessage = () => {
    showMessage({
      content: tips,
      type: 'warning'
    })
  }
  // 点击引导词
  // 点击推荐问题：填充输入并直接发送
  const handleClickGuideWord = message => {
    if (!showWhenLocked && inputLock.locked) {
      console.error('弹窗已锁定，消息无法发送')
      return
    }
    if (MentInputDiff) {
      inputRef.current.value = message
    } else {
      inputValue.current = message

      CustomDrinkTextAreaLoadingFun()
    }
    regenerate()
    handleSendClick()
  }
  useEffect(() => {
    if (quickQuestions) {
      handleClickGuideWord(quickQuestions)
    }
  }, [quickQuestions])
  // auto input height
  const handleTextAreaHeight = e => {
    const textarea = e.target
    textarea.style.height = 'auto'
    textarea.style.height = textarea.scrollHeight + 'px'
  }

  const handleSelectChat = e => {
    console.log(e, 'e')
  }
  // 推荐问题list
  const recomConversation = async e => {
    if (MentInputDiff) {
      inputRef.current.value = e.flow_description
    } else {
      inputValue.current = e.flow_description
      CustomDrinkTextAreaLoadingFun()
    }
    handleSendClick()
  }
  /* 鼠标滑动显示隐藏icon */
  const [mouseHid, setMouseHid] = useState(true)
  const recohandleMouseEnter = () => {
    setMouseHid(true)
  }
  const recohandelMouseLeave = () => {
    setMouseHid(true)
  }
  /* 重发字段 */
  const [is_regenerate, setIs_regenerate] = useState(false)
  /* switch 联网搜索 */
  const [disable_search, setDisable_search] = useState(false)
  /* switch 思考模式 */
  const [thinking_mode_switch, setThinking_mode_switch] = useState(1)
  const handleChange = checked => {
    // if (modelName === 'deepseek-r1') {
    //   showMessage({
    //     content: '智能体所选模型，当前暂不支持联网搜索',
    //     type: 'warning'
    //   })
    //   return
    // }
    setDisable_search(checked)
  }

  const [logoUrl, setLogoUrl] = useState('')
  const [OriginalUrl, setOriginalUrl] = useState('')
  const [imgName, setImgName] = useState('')

  /**
   * 上传图片：校验大小/类型，成功后设置展示与原图链接。
   */
  const handleImgUpload = async file => {
    try {
      setUpIsLoading(true)
      const formData = new FormData()
      formData.append('file', file)
      const response: any = await axios.post('/api/v1/common/upload/icon', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      console.log('文件上传成功:', response)
      currentUploadFileType.current = 'image'
      setImgName(file.name)
      // setLogoUrl(response?.file_path)
      setLogoUrl(response?.crop_file_path || response?.file_path)
      setOriginalUrl(response?.file_path)
      clearUploadedFileInfo()
      return response
    } catch (err) {
      console.error('Error uploading file:', err)
      throw err
    } finally {
      setUpIsLoading(false)
    }
  }

  /**
   * Antd Upload 自定义请求：图片上传
   */
  const uploadImage = async options => {
    const { onSuccess, onError, file, onProgress } = options
    try {
      const response: any = await handleImgUpload(file)
      onSuccess({ file, preview_url: response?.file_path })
    } catch (err) {
      console.error('Error uploading file:', err)
      onError({ err })
    }
  }

  /**
   * 图片上传配置：仅接收 .png/.jpg/.jpeg，大小 < 10MB（UI提示 5MB）
   */
  const UploadProps: UploadProps = {
    name: 'file',
    multiple: false,
    maxCount: 1,
    // defaultFileList,
    showUploadList: false,
    accept: '.png, .jpg, .jpeg',
    customRequest: uploadImage,
    beforeUpload: file => {
      // 文件上传前校验
      if (file.size > 5120000) {
        showMessage({
          content: '文件过大，请重新选择5M以内的文件',
          type: 'error'
        })
        return Upload.LIST_IGNORE
      }
      // const sizeOk = file.size / 1024 / 1024 < 10
      // if (!sizeOk) {
      //   showMessage({
      //     content: `${file.name} 超过${10}M,已移除`,
      //     type: 'warning'
      //   })
      //   return false
      // }
      const fileType = file.name.split('.').pop()?.toLowerCase()
      if (!['png', 'jpg', 'jpeg'].includes(fileType)) {
        showMessage({
          content: `不支持的文件类型${fileType},已移除`,
          type: 'warning'
        })
        return false
      }
      // if (!FILE_UPLOAD_ACCEPT_FILES.includes(fileType)) {
      //   showMessage({
      //     content: `不支持的文件类型${fileType},已移除`,
      //     type: 'warning'
      //   })
      //   return false
      // }
      return true
    },
    onChange(info) {
      const { status } = info.file
      if (status !== 'uploading') {
        // console.log(info.file, info.fileList)
      }
      if (status === 'done') {
        // onSelect(info.file.response.data.file_path)
        // message.success(`${info.file.name} file uploaded successfully.`)
      } else if (status === 'error') {
        // message.error(`${info.file.name} file upload failed.`)
      }
    },
    onDrop(e) {
      console.log('Dropped files', e.dataTransfer.files)
    },
    onRemove: file => {
      setLogoUrl('')
      setOriginalUrl('')
      setImgName('')
    }
  }

  const baseStyles = {
    // backgroundColor: conversation ? 'rgba(245,245,245)' : 'white'
    backgroundColor: 'rgba(245,245,245)'
  }

  const currentUploadFileType = useRef('')
  /** 已上传的文件列表，每项包含 id/fileId/fileName/fileUrl，最多 10 个 */
  const [uploadedFiles, setUploadedFiles] = useState<
    Array<{ id: string; fileId: any; fileName: string; fileUrl: string }>
  >([])
  /** 当前并发上传中的文件数量，所有上传完成后才关闭 loading */
  const uploadingCountRef = useRef(0)
  /** beforeUpload 已批准但 handleUpload 尚未结束的数量，用于批量多选时的实时上限检查 */
  const approvedCountRef = useRef(0)
  /** 同一批次文件选择中，超出数量限制的提示是否已弹出，防止重复弹窗 */
  const limitWarnedRef = useRef(false)
  /** 已上传文件列表滚动容器 ref */
  const fileListScrollRef = useRef<HTMLDivElement>(null)
  /** 文件列表左/右是否还有内容可滚动，控制箭头显示 */
  const [showLeftArrow, setShowLeftArrow] = useState(false)
  const [showRightArrow, setShowRightArrow] = useState(false)

  // 根据当前滚动位置刷新左右箭头显示状态
  const updateFileListArrows = useCallback(() => {
    const el = fileListScrollRef.current
    if (!el) return
    setShowLeftArrow(el.scrollLeft > 1)
    setShowRightArrow(el.scrollWidth - el.scrollLeft - el.clientWidth > 1)
  }, [])

  // 点击箭头时平滑横向滚动
  const scrollFileList = useCallback(
    (direction: 'left' | 'right') => {
      const el = fileListScrollRef.current
      if (!el) return
      const target =
        direction === 'left'
          ? Math.max(0, el.scrollLeft - 160)
          : Math.min(el.scrollWidth - el.clientWidth, el.scrollLeft + 160)
      el.scrollTo({ left: target, behavior: 'smooth' })
      // smooth 动画结束后浏览器不一定触发最后一次 scroll 事件，兜底补一次检测
      setTimeout(updateFileListArrows, 350)
    },
    [updateFileListArrows]
  )

  // 绑定滚轮事件（将纵向滚轮映射为横向，支持 Windows 鼠标），文件数量变化后重新检测箭头
  useEffect(() => {
    const el = fileListScrollRef.current
    if (!el) return
    const onWheel = (e: WheelEvent) => {
      if (e.deltaY === 0) return
      e.preventDefault()
      el.scrollLeft += e.deltaY
      updateFileListArrows()
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    // scrollend 在动画真正停止后触发（Chrome 114+ / Safari 16.4+），兜底 smooth 结束漏检的情况
    el.addEventListener('scrollend', updateFileListArrows)
    // useEffect 在 DOM 更新后执行，此时布局已完成，直接检测即可
    updateFileListArrows()
    return () => {
      el.removeEventListener('wheel', onWheel)
      el.removeEventListener('scrollend', updateFileListArrows)
    }
  }, [uploadedFiles.length, updateFileListArrows])

  /**
   * 清理已上传文件列表。
   */
  const clearUploadedFileInfo = () => {
    setUploadedFiles([])
  }
  /**
   * 上传文件：支持 pdf/doc/ppt 以及音频格式，返回 file_id 与可预览 URL。
   * 使用 uploadingCountRef 计数，所有并发上传都完成后才关闭 loading。
   */
  const handleUpload = async file => {
    uploadingCountRef.current += 1
    setUpIsLoading(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      const response: any = await axios.post('/api/v1/knowledge/upload_and_parse', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'chat-alias-id': currentConversationId.current,
          'assistant-id': assistantId
        }
      })
      console.log('文件上传成功:', response)
      currentUploadFileType.current = 'file'
      // 追加到文件列表，支持多文件并发上传
      setUploadedFiles(prev => [
        ...prev,
        { id: generateUUID(16), fileId: response.file_id, fileName: response.file_name, fileUrl: response.file_url }
      ])
      setImgName('')
      setLogoUrl('')
      setOriginalUrl('')
    } catch (err) {
      console.error('Error uploading file:', err)
      throw err
    } finally {
      approvedCountRef.current = Math.max(0, approvedCountRef.current - 1)
      uploadingCountRef.current -= 1
      if (uploadingCountRef.current === 0) {
        setUpIsLoading(false)
      }
    }
  }

  /**
   * Antd Upload 自定义请求：文件上传
   */
  const customUploadFile = async options => {
    const { onSuccess, onError, file, onProgress } = options
    try {
      await handleUpload(file)
      onSuccess({ file })
    } catch (err) {
      console.error('Error uploading file:', err)
      onError({ err })
    }
  }

  /**
   * 文件上传配置：接受文档/音频格式，大小 < 10MB
   */
  const upFileProps: UploadProps = {
    name: 'upFile',
    multiple: true,
    maxCount: 10,
    showUploadList: false,
    //  accept: pluginStatusMap.includedAudioPlugin
    // ? '.pdf, .docx, .doc, .ppt, .pptx, .wav, .mp3, .m4a, .ogg, .opus, .amr, .aac'
    // : '.doc, .docx, .pdf, .ppt, .pptx',
    accept: '.pdf, .docx, .doc, .ppt, .pptx, .wav, .mp3, .m4a, .ogg, .opus, .amr, .aac, .xlsx, .xls, .csv',
    customRequest: customUploadFile,
    onChange(info) {
      // loading 由 uploadingCountRef 统一控制，这里只负责展示错误提示
      if (info.file.status === 'error') {
        message.error(`${info.file.name} 上传失败`)
      }
    },
    beforeUpload: file => {
      // 用 approvedCountRef 计入同批次已放行但未完成的文件，避免 state 快照导致批量多选时超限
      if (uploadedFiles.length + approvedCountRef.current >= 10) {
        // 同一批次只弹一次提示，setTimeout(0) 在本轮同步任务结束后重置标志
        if (!limitWarnedRef.current) {
          limitWarnedRef.current = true
          showMessage({ content: '最多上传 10 个文件，请删除后再上传', type: 'warning' })
          setTimeout(() => {
            limitWarnedRef.current = false
          }, 0)
        }
        return false
      }
      const sizeOk = file.size / 1024 / 1024 < 10
      if (!sizeOk) {
        showMessage({
          content: `${file.name} 超过${10}M,已移除`,
          type: 'warning'
        })
        return false
      }

      const fileType = file.name.split('.').pop()?.toLowerCase()
      if (!FILE_UPLOAD_ACCEPT_FILES.includes(fileType)) {
        showMessage({
          content: `不支持的文件类型${fileType},已移除`,
          type: 'warning'
        })
        return false
      }
      // 只在通过所有校验后才计入，避免校验失败时泄漏计数
      approvedCountRef.current += 1
      return true
    },
    onDrop(e) {
      console.log('Dropped files', e.dataTransfer.files)
    },
    onRemove: file => {
      console.log('onRemove ', file)
      clearUploadedFileInfo()
    }
  }

  // 拖拽覆盖层已废弃，相关状态和事件不再使用（拖拽统一由外层 Chat/index.tsx 处理）
  // const [dragOver, setDragOver] = useState(false)
  // const onDragOver = useCallback(e => { e.preventDefault(); setDragOver(true) }, [])
  // const onDragLeave = useCallback(e => { e.preventDefault(); setDragOver(false) }, [])

  // 放下文件：校验类型与大小，区分图片与文件走不同上传逻辑
  const onDrop = useCallback(
    e => {
      e.preventDefault()
      // setDragOver(false)
      BusEvent.emit('chartFileDropComplete')
      const files = e.dataTransfer.files
      if (files.length === 0) return
      if (files.length > 1) {
        message.warning(`仅支持单个文件上传`)
        return
      }
      const file = files[0]
      let maxSize = 10
      let sizeLimit = file.size / 1024 / 1024 < 10
      const fileType = file.name.split('.').pop()?.toLowerCase()
      console.log('ACCEPT_FILES:', ACCEPT_FILES, !ACCEPT_FILES.includes(fileType))
      // if (!ACCEPT_FILES.includes(fileType)) {
      if (!ACCEPT_FILES_DROP.includes(fileType)) {
        showMessage({
          content: `不支持的文件类型${fileType},已移除`,
          type: 'warning'
        })
        return
      }
      // file详情页判断是否为图片格式
      const isImage = ['jpg', 'jpeg', 'png'].includes(fileType)
      if (isImage) {
        maxSize = 5
        sizeLimit = file.size / 1024 / 1024 < 5
      }
      if (!sizeLimit) {
        showMessage({
          content: `${file.name} 超过${maxSize}M,已移除`,
          type: 'warning'
        })
        return
      }
      if (isImage) {
        handleImgUpload(file)
      } else {
        handleUpload(file)
      }
      // handleUpload(file)
    },
    [pluginStatusMap.includedAudioPlugin]
  )

  useEffect(() => {
    BusEvent.on('chartFileDrop', onDrop)
    return () => {
      BusEvent.off('chartFileDrop', onDrop)
    }
  }, [onDrop])

  const [overlayVisible, setOverlayVisible] = useState(false)
  const showOverlay = () => {
    setOverlayVisible(true)
  }

  const hideOverlay = () => {
    setOverlayVisible(false)
  }

  // 判断是否为通义万相wan2.6模型
  const isWanxiangModel = modelName === '通义万相wan2.6'

  // 通义万相上传文件状态（提升到 ChatInput 以便在输入框上方显示）
  const [uploadedVideos, setUploadedVideos] = useState<Array<{ type: string; url: string; name: string }>>([])
  const [uploadedAudio, setUploadedAudio] = useState<{ type: string; url: string; name: string } | null>(null)
  // 使用 ref 存储当前值，确保在异步操作中能读取到最新值
  const uploadedVideosRef = useRef<Array<{ type: string; url: string; name: string }>>([])
  const uploadedAudioRef = useRef<{ type: string; url: string; name: string } | null>(null)
  const logoUrlRef = useRef<string>('')
  const imgNameRef = useRef<string>('')

  // 同步状态到 ref，确保 ref 始终是最新值
  useEffect(() => {
    uploadedVideosRef.current = uploadedVideos
  }, [uploadedVideos])

  useEffect(() => {
    uploadedAudioRef.current = uploadedAudio
  }, [uploadedAudio])

  useEffect(() => {
    logoUrlRef.current = logoUrl
  }, [logoUrl])

  useEffect(() => {
    imgNameRef.current = imgName
  }, [imgName])
  // 通义万相配置状态
  const [wanxiangConfig, setWanxiangConfig] = useState({
    smartRewrite: true, // 智能改写，默认开启
    resolution: '1080P', // 分辨率，默认1080P
    duration: '5', // 视频时长，默认5秒
    cameraMode: 'single', // 镜头模式，默认单镜头
    generateAudio: false // 生成音频，默认生成
  })

  // 新增音频播放相关状态
  const [audioPlaying, setAudioPlaying] = useState(false)
  const audioRef = useRef<HTMLAudioElement>(null)
  // 通义万相音频播放相关
  const wanxiangAudioId = useRef(generateUUID(16))
  const wanxiangAudioRef = useRef<HTMLAudioElement>(null)

  // 文件预览相关
  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewFile, setPreviewFile] = useState<FileInfoType>()
  // 文件预览：打开预览抽屉
  const handlePreview = (title, url) => {
    console.log('handlePreview', title, url)
    const fileInfo = {
      file_name: title,
      file_path: url
    }
    setPreviewFile(fileInfo)
    setPreviewOpen(true)
  }

  // 音频互斥播放：当播放ID不是本组件时，暂停当前音频
  useEffect(() => {
    // 监听音频播放事件
    if (audioPlayingId !== audioId.current && audioRef.current) {
      audioRef.current.pause() // 暂停之前的音频
    }
    if (audioPlayingId !== wanxiangAudioId.current && wanxiangAudioRef.current) {
      wanxiangAudioRef.current.pause() // 暂停通义万相音频
    }
  }, [audioPlayingId])

  return (
    <div className="absolute bottom-[16px] w-full pt-1 dark:bg-[#1B1B1B]" style={{ ...baseStyles }}>
      {/* 推荐问题 */}
      {recommendVisible && (
        <RecommendedCont
          mouseHid={mouseHid}
          conversation={conversation}
          regenerate={() => {
            regenerate()
            setTimeout(() => {
              // 滚动聊天对话
              const messageDom = document.getElementById('message-panne')
              if (messageDom) {
                messageDom.scrollTop = messageDom.scrollHeight
              }
            }, 500)
          }}
          recomConversation={recomConversation}
          recommendedHid={recommendedHid}
          list={questions}
          chatId={chatId}
          locked={inputLock.locked}
          chatsRef={'RecommendedCont'}
          handleSelectChat={handleSelectChat}
          onClick={handleClickGuideWord}
          recohandleMouseEnter={recohandleMouseEnter}
          recohandelMouseLeave={recohandelMouseLeave}
        ></RecommendedCont>
      )}

      {/* <div className={`relative ${clear && 'pl-9'}`}> */}
      <div
        className={'relative'}
        style={{ bottom: '0' }}
        // onDragOver={onDragOver}
        // onDragLeave={onDragLeave}
        // onDrop={onDrop}
      >
        {/* 拖拽覆盖层已废弃，拖拽逻辑统一由外层 Chat/index.tsx 处理 */}
        {/* {dragOver && (
          <div className="chat-file-upload-container flex flex-col items-center justify-center">
            <div className="flex flex-col items-center justify-center">
              <div className="common-upload-main-text">文件拖拽到此处可上传</div>
              <div className="common-upload-sub-text mx-[32px] text-start">
                支持上传的文件(大小不能超过10MB),接受.pdf（支持解析50页内容）, .docx, .doc, .ppt, .pptx, .wav, .mp3,
                .m4a, .ogg, .opus, .amr, .aac格式文件
              </div>
            </div>
          </div>
        )} */}
        {/* form */}
        {formShow && (
          <div className="relative">
            <div className="absolute bottom-2 left-0 w-[50%] min-w-80 rounded-md border bg-[#fff] px-4 py-2">
              {inputForm}
            </div>
          </div>
        )}
        {/* send */}
        <div
          className={
            // conversation ? 'chatInput_case_absIcon absolute z-10 flex gap-2' : 'absolute right-3 top-4 z-10 flex gap-2'
            'chatInput_case_absIcon absolute z-10 flex gap-2'
          }
        >
          <img
            src={MicrophoneIcon}
            alt=""
            className="cursor-pointer"
            onClick={() => {
              !inputLock.locked && showOverlay()
            }}
          />
          <div
            id="bs-send-btn"
            className="flex cursor-pointer items-center justify-center rounded-sm"
            onClick={() => {
              if (disabled) {
                noLimitsMessage()
                return
              }
              !inputLock.locked && handleSendClick()
            }}
          >
            {SendBsSendBtnSVG()}
          </div>
        </div>
        {/* question */}
        <div className="chatInput_case flexCol">
          <div className="flexRowStart flexRowJustBet">
            <div className="flexRowStart">
              {/* 联网搜索 */}
              {supportSearch && !isWanxiangModel && (
                <div className="flexRowStart">
                  <span>联网搜索</span>
                  <div className="applicationEdit_switchCase">
                    <SwitchComponent
                      style={{ marginLeft: '4px' }}
                      checked={disable_search}
                      onChange={handleChange}
                      size="small"
                      disabled={running}
                    />
                  </div>
                  <Divider type="vertical" />
                </div>
              )}
              {/* 上传图片 - 非通义万相模型时显示 */}
              {!isWanxiangModel && (
                <div className="chatInput_upload_def cursonCase">
                  <Upload {...UploadProps} disabled={running}>
                    <Tooltip title={'上传图片(大小不超过5MB，支持.jpg/.jpeg/.png)'}>{UploadImgDefSVG()}</Tooltip>
                  </Upload>
                </div>
              )}
              {/* 上传文件 - 非通义万相模型时显示 */}
              {!isWanxiangModel && (
                <div className="chat-file-upload-def">
                  <Upload {...upFileProps} disabled={running}>
                    <Tooltip
                      title={
                        '支持上传的文件(大小不能超过10MB),接受.pdf（支持解析50页内容）, .docx, .doc, .ppt, .pptx, .wav, .mp3, .m4a, .ogg, .opus, .amr, .aac, .xlsx, .xls, .csv格式文件'
                      }
                    >
                      {UploadFileDefSVG()}
                    </Tooltip>
                  </Upload>
                </div>
              )}
              {/* @人= 插件 */}
              {!MentInputDiff && (
                <div
                  className="cursonCase mr-[8px]"
                  onClick={() => {
                    sendInputRef.current?.insertCurlyBracket()
                  }}
                >
                  {MentINputDiffSVG()}
                </div>
              )}
              {/* 思考模式 */}
              {deep_think_mode === 1 && (
                <div className="flexRowStart">
                  <span>思考模式</span>
                  <div className="applicationEdit_switchCase">
                    <SwitchComponent
                      style={{ marginLeft: '4px' }}
                      checked={thinking_mode_switch === 1}
                      onChange={e => {
                        setThinking_mode_switch(e ? 1 : 0)
                      }}
                      size="small"
                      disabled={running}
                    />
                  </div>
                  <Divider type="vertical" />
                </div>
              )}
              {/* 通义万相wan2.6 上传功能 */}
              {isWanxiangModel && (
                <WanxiangUpload
                  running={running}
                  assistantId={assistantId}
                  currentConversationId={currentConversationId.current}
                  setUpIsLoading={setUpIsLoading}
                  logoUrl={logoUrl}
                  setLogoUrl={setLogoUrl}
                  setOriginalUrl={setOriginalUrl}
                  setImgName={setImgName}
                  UploadProps={UploadProps}
                  uploadedVideos={uploadedVideos}
                  setUploadedVideos={setUploadedVideos}
                  uploadedAudio={uploadedAudio}
                  setUploadedAudio={setUploadedAudio}
                  wanxiangConfig={wanxiangConfig}
                  setWanxiangConfig={setWanxiangConfig}
                />
              )}
            </div>
            {model_icon && (
              <span className="chatInput_case_title flexRowStart">
                <span className="mr-[4px]">模型</span>
                <Tooltip
                  title={
                    <div className="whitespace-pre-line">
                      {`${modelName || '--'}`}
                      {/* {modelName === 'deepseek-r1' || modelName === 'Qwen3-235B' ? '\n联网搜索使用百应自研服务' : ''} */}
                    </div>
                  }
                  style={{ maxWidth: '300px' }}
                >
                  <div className="cursonCase">
                    <img src={model_icon} alt="模型图标" className="h-[16px] w-[16px]" />
                  </div>
                </Tooltip>
              </span>
            )}
          </div>
          {/* 显示上传的视频（通义万相） */}
          {isWanxiangModel && uploadedVideos.length > 0 && (
            <div className="flexRowStart chat-input-file-upload-case mt-[54px] h-[48px]">
              {uploadedVideos.map((video, index) => (
                <div
                  className="flexRowStart chatInput_case_uploadposCase mr-[8px] h-[48px]"
                  key={`video-${index}-${video.name}-${video.url}`}
                  style={{ gap: '8px' }}
                >
                  {WanxiangUploadSVG()}
                  <div className="relative flex gap-[8px]">
                    <Tooltip title={video.name}>
                      <div className="cursor-pointer" onClick={() => handlePreview(video.name, video.url)}>
                        {formatFileName(video.name)}
                      </div>
                    </Tooltip>
                    <svg
                      className="chat-file-remove-icon cursonCase"
                      onClick={() => {
                        setUploadedVideos(prev => prev.filter((_, i) => i !== index))
                      }}
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle cx="7" cy="7" r="7" fill="#C8C8C9" />
                      <path
                        d="M5 5L9 9"
                        stroke="white"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M5 9L9 5"
                        stroke="white"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* 显示上传的音频（通义万相） */}
          {isWanxiangModel && uploadedAudio && (
            <div
              className="flexRowStart chat-input-file-upload-case mt-[54px] h-[48px]"
              style={logoUrl ? { marginLeft: '76px', marginTop: '46px' } : {}}
            >
              <div className="flexRowStart chatInput_case_uploadposCase h-[48px]">
                <img src={getFileIconByFileName(uploadedAudio.name)} alt="" className="h-[32px] w-[32px]" />
                <div className="relative flex gap-[8px]">
                  {['wav', 'mp3', 'm4a', 'ogg', 'opus', 'amr', 'aac'].includes(getFileType(uploadedAudio.name)) ? (
                    <Tooltip title={uploadedAudio.name}>
                      <div className="pt-[2px]">{formatFileName(uploadedAudio.name)}</div>
                    </Tooltip>
                  ) : (
                    <Tooltip title={uploadedAudio.name}>
                      <div
                        className="cursor-pointer"
                        onClick={() => handlePreview(uploadedAudio.name, uploadedAudio.url)}
                      >
                        {formatFileName(uploadedAudio.name)}
                      </div>
                    </Tooltip>
                  )}
                  {/* 音频播放按钮和audio标签 */}
                  {['wav', 'mp3', 'm4a', 'ogg', 'opus', 'aac'].includes(getFileType(uploadedAudio.name)) && (
                    <>
                      {audioPlayingId !== wanxiangAudioId.current && (
                        <span
                          className="cursor-pointer"
                          onClick={() => {
                            if (wanxiangAudioRef.current) {
                              wanxiangAudioRef.current.play()
                            }
                          }}
                        >
                          {AudioPlaySvg}
                        </span>
                      )}
                      {audioPlayingId === wanxiangAudioId.current && (
                        <div className="group relative">
                          <div className="audio-running-div block group-hover:hidden">{RunningSvg}</div>
                          <div
                            className="hidden cursor-pointer group-hover:block"
                            onClick={() => {
                              if (wanxiangAudioRef.current) {
                                wanxiangAudioRef.current.pause()
                              }
                            }}
                          >
                            {PauseSvg}
                          </div>
                        </div>
                      )}
                      <audio
                        ref={wanxiangAudioRef}
                        src={uploadedAudio.url}
                        style={{ display: 'none' }}
                        onPlay={() => setAudioPlayingId(wanxiangAudioId.current)}
                        onPause={() => {
                          // 只有当前播放的audio才清空
                          if (audioPlayingId === wanxiangAudioId.current) setAudioPlayingId(null)
                        }}
                        onEnded={() => setAudioPlayingId(null)}
                      />
                    </>
                  )}
                  {/* amr文件用原有播放器 */}
                  {['amr'].includes(getFileType(uploadedAudio.name)) && <AmrPlayer audioUrl={uploadedAudio.url} />}
                  <svg
                    className="chat-file-remove-icon cursonCase top-[-15px]"
                    onClick={() => {
                      setUploadedAudio(null)
                      uploadedAudioRef.current = null
                    }}
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="7" cy="7" r="7" fill="#C8C8C9" />
                    <path d="M5 5L9 9" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M5 9L9 5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              </div>
            </div>
          )}

          {logoUrl && (
            <div className={`flexRowStart chatInput_case_uploadCase ${isWanxiangModel ? 'mt-[54px]' : ''}`}>
              <div className="chatInput_case_uploadposCase">
                {isWanxiangModel ? (
                  <img src={logoUrl} alt="图片" />
                ) : (
                  <img src={logoUrl} alt="图片" style={{ border: '1px solid #DDE3F2' }} />
                )}
                <svg
                  className="chatInput_loadIcon cursonCase"
                  onClick={() => {
                    setLogoUrl('')
                    setOriginalUrl('')
                    setImgName('')
                    logoUrlRef.current = ''
                    imgNameRef.current = ''
                  }}
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="7" cy="7" r="7" fill="#C8C8C9" />
                  <path d="M5 5L9 9" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M5 9L9 5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            </div>
          )}

          <div className="textarea-container" onMouseDown={onInputClick}>
            {logoUrl ||
            uploadedFiles.length > 0 ||
            (isWanxiangModel && (uploadedVideos.length > 0 || uploadedAudio)) ? (
              <div className="temp-file-mask"></div>
            ) : (
              ''
            )}
            {/* 已上传文件列表：移入 textarea-container（position:relative）内，
                left/right 相对于 textarea 计算，彻底避免超出输入框两侧。
                内层滚动区 paddingTop:8 为卡片外 top:-18px 的删除 icon 留空间 */}
            {uploadedFiles.length > 0 && (
              <div
                className="chat-input-file-upload-case"
                style={{
                  top: 13,
                  left: 16,
                  right: 16,
                  marginTop: 0,
                  marginLeft: 0,
                  boxSizing: 'border-box'
                }}
              >
                {/* 文件卡片横向滚动区 */}
                <div
                  ref={fileListScrollRef}
                  className="caseOverFlowX"
                  style={{
                    display: 'flex',
                    gap: 8,
                    flexWrap: 'nowrap',
                    paddingTop: 8,
                    paddingLeft: 4,
                    paddingRight: 28
                  }}
                  onScroll={updateFileListArrows}
                >
                  {uploadedFiles.map(f => {
                    const fType = getFileType(f.fileName)
                    const audioKey = f.id
                    return (
                      <div key={f.id} className="flexRowStart chatInput_case_uploadposCase" style={{ flexShrink: 0 }}>
                        <img src={getFileIconByFileName(f.fileName)} alt="" className="h-[32px] w-[32px]" />
                        <div className="relative flex gap-[8px]">
                          <Tooltip title={f.fileName}>
                            <div
                              className="cursor-pointer"
                              onClick={() => {
                                if (!['wav', 'mp3', 'm4a', 'ogg', 'opus', 'amr', 'aac'].includes(fType)) {
                                  handlePreview(f.fileName, f.fileUrl)
                                }
                              }}
                            >
                              {formatFileName(f.fileName)}
                            </div>
                          </Tooltip>
                          {['wav', 'mp3', 'm4a', 'ogg', 'opus', 'aac'].includes(fType) && (
                            <>
                              {audioPlayingId !== audioKey && (
                                <span
                                  className="cursor-pointer"
                                  onClick={() => {
                                    const el = document.getElementById(`chat-upload-audio-${f.id}`) as HTMLAudioElement
                                    el?.play()
                                  }}
                                >
                                  {AudioPlaySvg}
                                </span>
                              )}
                              {audioPlayingId === audioKey && (
                                <div className="group relative">
                                  <div className="audio-running-div block group-hover:hidden">{RunningSvg}</div>
                                  <div
                                    className="hidden cursor-pointer group-hover:block"
                                    onClick={() => {
                                      const el = document.getElementById(
                                        `chat-upload-audio-${f.id}`
                                      ) as HTMLAudioElement
                                      el?.pause()
                                    }}
                                  >
                                    {PauseSvg}
                                  </div>
                                </div>
                              )}
                              <audio
                                id={`chat-upload-audio-${f.id}`}
                                src={f.fileUrl}
                                style={{ display: 'none' }}
                                onPlay={() => setAudioPlayingId(audioKey)}
                                onPause={() => {
                                  if (audioPlayingId === audioKey) setAudioPlayingId(null)
                                }}
                                onEnded={() => setAudioPlayingId(null)}
                              />
                            </>
                          )}
                          {['amr'].includes(fType) && <AmrPlayer audioUrl={f.fileUrl} />}
                          <svg
                            className="chat-file-remove-icon cursonCase"
                            onClick={() => setUploadedFiles(prev => prev.filter(item => item.id !== f.id))}
                            width="14"
                            height="14"
                            viewBox="0 0 14 14"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <circle cx="7" cy="7" r="7" fill="#C8C8C9" />
                            <path
                              d="M5 5L9 9"
                              stroke="white"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M5 9L9 5"
                              stroke="white"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                      </div>
                    )
                  })}
                </div>
                {/* 左滚动箭头，绝对定位在容器左侧 */}
                {showLeftArrow && (
                  <div
                    className="chat-file-arrow-btn chat-file-arrow-btn--left cursonCase"
                    onClick={() => scrollFileList('left')}
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path
                        d="M8 10L4 6L8 2"
                        stroke="#131921"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                )}
                {/* 右滚动箭头，绝对定位在容器右侧 */}
                {showRightArrow && (
                  <div
                    className="chat-file-arrow-btn chat-file-arrow-btn--right cursonCase"
                    onClick={() => scrollFileList('right')}
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path
                        d="M4 10L8 6L4 2"
                        stroke="#131921"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                )}
              </div>
            )}
            {MentInputDiff ? (
              <Textarea
                id="bs-send-input"
                ref={inputRef}
                rows={1}
                disabled={inputLock.locked}
                onInput={handleTextAreaHeight}
                onDrop={e => {
                  // 阻止默认行为，防止在输入框中插入文件内容
                  e.preventDefault()
                  // 不阻止事件冒泡，让容器级别处理上传
                }}
                onDragOver={e => {
                  // 阻止默认行为，允许拖拽
                  e.preventDefault()
                }}
                placeholder={
                  inputLock.locked
                    ? inputLock.reason
                    : isWanxiangModel
                    ? '描述视频画面内容和动态过程，支持文生视频、图生视频、视频生视频三种模式'
                    : '请输入问题或将文件、图片拖拽至对话框内，Enter发送，Shift+Enter换行'
                }
                className={`text-md dark:bg-[#2A2B2E]' + (form && ' pl-10') min-h-6 resize-none py-4 scrollbar-hide  ${
                  logoUrl ||
                  uploadedFiles.length > 0 ||
                  (isWanxiangModel && (uploadedVideos.length > 0 || uploadedAudio))
                    ? 'h-[180px] pt-[74px]'
                    : 'h-[118px]'
                }`}
                style={{
                  height: '56px',
                  marginTop: '12px'
                }}
                onKeyDown={event => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault()
                    !inputLock.locked && handleSendClick()
                  }
                }}
              ></Textarea>
            ) : (
              <div
                // className={
                //   'text-md h-[118px] min-h-6 resize-none scrollbar-hide dark:bg-[#2A2B2E]' + (form && ' pl-10')
                // }
                className={`text-md dark:bg-[#2A2B2E]' + (form && ' pl-10') h-[118px] min-h-6 resize-none scrollbar-hide  ${
                  logoUrl ||
                  uploadedFiles.length > 0 ||
                  (isWanxiangModel && (uploadedVideos.length > 0 || uploadedAudio))
                    ? 'h-[180px]'
                    : 'h-[118px]'
                }`}
                id="bs-send-input"
                style={{
                  boxSizing: 'border-box',
                  marginTop: '12px'
                }}
              >
                {CustomDrinkTextAreaLoading && (
                  <ChatSendInput
                    onInputClick={onInputClick}
                    disabled={inputLock.locked}
                    placeholder={
                      inputLock.locked
                        ? inputLock.reason
                        : '请输入问题或将文件、图片拖拽至对话框内，Enter发送，Shift+Enter换行'
                    }
                    ref={sendInputRef}
                    newValue={ensureTextAtEnd(parseHtmlToSlateValue(inputValue.current))}
                    save={(e: any) => {
                      const all = e.map((node: any) => extractTextFromNode(node))
                      const plainText = all.map(a => a.plain).join('\n')
                      const htmlText = all.map(a => a.html).join('<br/>')
                      // 合并所有 idList（保留重复项）
                      const mergedIdList = all.flatMap(a => a.idList)
                      // 是否包含 'all'
                      const allType = all.some(a => a.allType === 'all') ? 'all' : ''
                      inputValue.current = htmlText
                      /* send选择人员判断 */
                      const notifyInfo = {
                        user_datas: mergedIdList,
                        is_all: !!allType
                      }
                      setNotifyInfo({
                        ...notify_info,
                        ...notifyInfo
                      })
                    }}
                    enterFun={() => {
                      !inputLock.locked && handleSendClick()
                    }}
                    sendValue={inputValue.current}
                    containsFile={
                      !!(
                        logoUrl ||
                        uploadedFiles.length > 0 ||
                        (isWanxiangModel && (uploadedVideos.length > 0 || uploadedAudio))
                      )
                    }
                  ></ChatSendInput>
                )}
              </div>
            )}
            <SpeechRecognizer
              visible={overlayVisible}
              onClose={hideOverlay}
              onRecognizedText={handleRecognizedText}
            ></SpeechRecognizer>
          </div>

          <div className="chatInput_footer_tips">内容由AI生成，请仔细甄别，无法确保真实准确。</div>
        </div>
      </div>
      {previewOpen && <PreviewPage open={previewOpen} fileInfo={previewFile} setOpen={setPreviewOpen} />}
    </div>
  )
}
